import { createAsyncThunk } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { FormatService } from '@vk-hr-tek/core/format';
import { pollDownloadStatus } from '@vk-hr-tek/core/download-notification/slice';
import { setDownloadWaiting } from '@vk-hr-tek/core/download-notification';

import {
  CreateEventBatchDocumentZipResponse,
  EventPdfResponse,
  CreateEventBatchDocumentZipRequest,
  EventsService,
  EventListRequestFilters,
} from '@app/gen/events';

import { ThunkExtra } from '../../../app/store';
import { EventsDownloadService } from '../../services';
import { GetEventsPdfDto } from '../../dto';

interface BatchDownloadStartDto extends CreateEventBatchDocumentZipRequest {
  totalCount?: number;

  filters?: EventListRequestFilters;
}

export const startDownload = createAsyncThunk<
  CreateEventBatchDocumentZipResponse,
  BatchDownloadStartDto,
  ThunkExtra
>(
  'events/startBatchDownload',
  async (
    batchDownloadStartDto: BatchDownloadStartDto,
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsDownloadService);
      const eventService = inject(EventsService);

      const { totalCount, filters } = batchDownloadStartDto;

      let downloadId;
      if (totalCount) {
        const result = await eventService.operationEvents({
          body: {
            ...(filters && { filters }),
            operation: 'batch_download',
          },
        });

        downloadId = result.download_id || '';
      } else {
        const result = await service.startDownload(batchDownloadStartDto);

        downloadId = result.download_id;
      }

      await dispatch(
        setDownloadWaiting({
          id: downloadId,
          count: totalCount || batchDownloadStartDto.event_ids.length,
        }),
      );

      dispatch(
        pollDownloadStatus({
          download_id: downloadId,
        }),
      );

      return {
        download_id: downloadId,
      };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadEventsPdfDocument = createAsyncThunk<
  EventPdfResponse,
  GetEventsPdfDto,
  ThunkExtra
>(
  'events/startDownloadEventsPdf',
  async (
    getEventsPdfDto: GetEventsPdfDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsDownloadService);
      const format = inject(FormatService);

      const state = getState().events;

      const { total, filters } = state;

      const totalSelected = getEventsPdfDto.event_ids?.length;

      const { totalCount } = getEventsPdfDto;
      const body = {
        ...(!totalCount && { event_ids: getEventsPdfDto.event_ids }),
        ...(totalCount && { filters: getEventsPdfDto.filters }),
      };
      const result = await service.startDownloadEventsPdf(
        body,
        filters.data || [],
      );

      const formattedTotal = format.pluralize(
        totalCount ? total : totalSelected || 0,
        ['заявка', 'заявки', 'заявок'],
      );

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: totalCount ? totalCount : total,
          text: {
            loading: `Создаем файл (${formattedTotal})`,
            complete: `Файл создан  (${formattedTotal})`,
            failed: 'Ошибка создания файла',
          },
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);
